import { useState, useRef } from "react";
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import { CheckIcon } from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import { useClickOutside } from "hooks";
import clsx from "clsx";

export function CustomDropdown({
  dropdownKey,
  options = [],
  selectedValue,
  onSelect,
  placeholder = "Select...",
  showSearch = true,
  searchThreshold = 5,
  className = "",
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useClickOutside(dropdownRef, () => setIsOpen(false));

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const getFilteredOptions = (options, searchTerm) => {
    if (!searchTerm) return options || [];
    return (options || []).filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  };

  const filteredOptions = getFilteredOptions(options, searchTerm);
  const selectedOption = options?.find((opt) => opt.value === selectedValue);
  const shouldShowSearch =
    showSearch && options && options.length > searchThreshold;

  const handleSelect = (value) => {
    onSelect?.(value);
    setIsOpen(false);
    setSearchTerm(""); // Reset search term
  };

  return (
    <div className={clsx("relative", className)} ref={dropdownRef}>
      <Button
        variant="outlined"
        className={clsx(
          "flex w-full items-center justify-between gap-2 border-gray-300 bg-white px-3 py-2 text-sm transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700",
          selectedValue &&
            "border-primary-500 bg-primary-50 dark:bg-primary-900/20",
          disabled && "cursor-not-allowed opacity-50",
        )}
        onClick={toggleDropdown}
        disabled={disabled}
      >
        <span
          className={clsx(
            "truncate",
            selectedValue
              ? "text-primary-700 dark:text-primary-300 font-medium"
              : "text-gray-500 dark:text-gray-400",
          )}
        >
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDownIcon
          className={clsx(
            "size-4 transition-transform",
            isOpen && "rotate-180",
          )}
        />
      </Button>

      {isOpen && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
          {/* Search */}
          {shouldShowSearch && (
            <div className="border-b border-gray-200 p-2 dark:border-gray-600">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute top-1/2 left-3 size-4 -translate-y-1/2 transform text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="focus:ring-primary-500 w-full rounded-md border border-gray-200 bg-gray-50 py-2 pr-3 pl-9 text-sm text-gray-900 focus:ring-2 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
              </div>
            </div>
          )}

          {/* Options */}
          <div className="sm:max-h-28 max-h-48 overflow-auto">
            {filteredOptions.map((option) => (
              <button
                key={option.value}
                className={clsx(
                  "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700",
                  selectedValue === option.value &&
                    "bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300",
                )}
                onClick={() => handleSelect(option.value)}
              >
                {selectedValue === option.value && (
                  <CheckIcon className="text-primary-600 size-4" />
                )}
                <span
                  className={selectedValue === option.value ? "ml-0" : "ml-6"}
                >
                  {option.label}
                  {option.count !== undefined && (
                    <span className="ml-2 text-xs text-gray-500">
                      ({option.count})
                    </span>
                  )}
                </span>
              </button>
            ))}

            {filteredOptions.length === 0 && searchTerm && (
              <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                No options found for `${searchTerm}`
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
